// ----------------------------------------------------------------------------
// Component: Content Panel
// ----------------------------------------------------------------------------
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Scripts
import Swiper, { Navigation, Pagination, A11y, Keyboard } from 'swiper';
import 'snippets/icon/icon';
import 'snippets/swiper-control/swiper-control';
import 'snippets/background-media/background-media';

// Style includes
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/a11y';
import 'swiper/css/keyboard';
import './_content-panel.scss';

// Content Panel - Video
import 'snippets/video-player/video-block';
import 'snippets/_containers/modal/modal';

// -------------------------------------
// Variables
// -------------------------------------

// Selectors
const CONTENT_PANEL_SELECTOR          = '.content-panel';
const CAROUSEL_WRAPPER_SELECTOR       = '.content-panel-carousel__wrapper';
const CAROUSEL_SELECTOR               = '.carousel---content-panel';
const SWIPER_WRAPPER_SELECTOR         = '.swiper-wrapper';
const SWIPER_CONTROLS_SELECTOR        = '.swiper-controls';
const SWIPER_ARROW_SELECTOR           = '.swiper-arrow';
const SWIPER_ARROW_CONTAINER_SELECTOR = '.swiper-arrow__container';
const PREV_BUTTON_SELECTOR            = '.swiper-button-prev';
const NEXT_BUTTON_SELECTOR            = '.swiper-button-next';
const CONTENT_MEDIA_SELECTOR          = '.content-panel__meta';
const CONTENT_TEXT_SELECTOR           = '.content-panel__content';
const SWIPER_PAGINATION_SELECTOR      = '.swiper-pagination';
const SWIPER_GOOEY_SELECTOR           = '.gooey-container';

// Classes
const IS_SLIDER                   = 'is-slider';
const ARROW_INIT_CLASS            = 'swiper-arrow-initialized';

// Other
const SLIDES_PER_VIEW            = 1;


// -------------------------------------
// Helpers
// -------------------------------------


const initContentPanel = (ContentPanel) => {
    if (ContentPanel.classList.contains(IS_SLIDER)) {
        initContentPanelSwiper(ContentPanel);
    }
};

const initContentPanelSwiper = (carousel) => {
    const carouselWrapper = carousel.querySelector(CAROUSEL_WRAPPER_SELECTOR);
    const sectionID       = carousel.dataset.sectionId;
    const parentSelector  = CONTENT_PANEL_SELECTOR + '--' + sectionID;
    const swiperArrows    = carouselWrapper.querySelectorAll(SWIPER_ARROW_SELECTOR);
    const gooeyAnimationContainer = carouselWrapper.querySelector(SWIPER_GOOEY_SELECTOR);

    gooeyAnimationContainer.style.display = 'flex';

    addClassToElements(swiperArrows, ARROW_INIT_CLASS);

    const swiper = new Swiper(parentSelector + ' ' + CAROUSEL_SELECTOR, {
        modules: [Navigation, Pagination, A11y, Keyboard],
        slidesPerView: SLIDES_PER_VIEW,
        spaceBetween: 10,
        observer: true,
        observeParents: true,
        a11y: true,
        watchOverflow: true,
        navigation: {
            nextEl: parentSelector + ' ' + NEXT_BUTTON_SELECTOR,
            prevEl: parentSelector + ' ' + PREV_BUTTON_SELECTOR
        },
        pagination: {
            el: parentSelector + ' ' + SWIPER_PAGINATION_SELECTOR,
            type: 'bullets',
            clickable: true
        },
        on: {
            beforeTransitionStart: function (swiper) {
                const previousIndex = swiper.previousIndex;
                const activeIndex   = swiper.activeIndex;
                const bulletIndex   = swiper.activeIndex + 1;
                const nextBullet    = carouselWrapper.querySelector(`.swiper-pagination-bullet:nth-child(${bulletIndex})`);
                const gooeySlider   = carouselWrapper.querySelector('.gooey-slider');

                if (nextBullet !== null && gooeySlider !== null) {
                    const offsetLeft = (previousIndex < activeIndex) ? nextBullet.offsetLeft : nextBullet.offsetLeft;

                    gooeySlider.style.left = `${offsetLeft}px`;
                }
            },
        }
    });

    swiper.slides.forEach((slide, index) => {
        const slideLink = slide.querySelector('a');

        if(slideLink !== null) {
            slideLink.addEventListener('focus', (e) => {
                swiper.slideTo(index);
            });
        }
    });
};

const initSwiperControl = (carousel) => {
    const carouselWrapper = carousel.querySelector(CAROUSEL_WRAPPER_SELECTOR);
    const swiperControls  = carouselWrapper.querySelector(SWIPER_CONTROLS_SELECTOR);
    const swiperArrowsContainer   = carouselWrapper.querySelectorAll(SWIPER_ARROW_CONTAINER_SELECTOR);
    const contentTextContainer    = carouselWrapper.querySelectorAll(CONTENT_TEXT_SELECTOR);
    const swiperWrapper           = carouselWrapper.querySelector(SWIPER_WRAPPER_SELECTOR);
    const swiperWrapperRect       = swiperWrapper.getBoundingClientRect();
    const contentMediaContainer        = carouselWrapper.querySelector(CONTENT_MEDIA_SELECTOR);
    const contentMediaContainerHeight  = contentMediaContainer.offsetHeight;
    const swiperArrowsTop              = contentMediaContainerHeight / 2 - 27.5;
    let largestContentTextContainerBtm = -Infinity; // Start with a very small value
    let contentTextContainerWidth      = 'auto';

    if( swiperArrowsContainer != null ) {
        swiperArrowsContainer.forEach((swiperArrowContainer) => {
            swiperArrowContainer.style.top = swiperArrowsTop + 'px';
        });
    }

    if( contentTextContainer != null ) {
        contentTextContainer.forEach((contentText) => {
            const contentTextContainerRect    = contentText.getBoundingClientRect();
            const contentTextContainerBtm     = contentTextContainerRect.bottom - swiperWrapperRect.top;
            contentTextContainerWidth   = contentText.offsetWidth;

            if (contentTextContainerBtm > largestContentTextContainerBtm) {
                largestContentTextContainerBtm = contentTextContainerBtm;
            }
        });
    }

    const largestBtmValue = largestContentTextContainerBtm;

    if( swiperControls != null ) {
        swiperControls.style.top = largestBtmValue + 'px';
        swiperControls.style.width = contentTextContainerWidth + 'px';
    }
};

const addClassToElements = (elems, className) => {
    elems.forEach((elem) => {
        elem.classList.add(className);
    });
};

// -------------------------------------
// Hooks
// -------------------------------------

document.addEventListener('DOMContentLoaded', () => {
    const ContentPanels = document.querySelectorAll(CONTENT_PANEL_SELECTOR);

    ContentPanels.forEach((ContentPanel) => {
        initContentPanel(ContentPanel);
        initSwiperControl(ContentPanel);
    });
});

let resizeTimeout;
window.addEventListener('resize', function() {
    clearTimeout(resizeTimeout);

    resizeTimeout = setTimeout(() => {
        const ContentPanels = document.querySelectorAll(CONTENT_PANEL_SELECTOR);

        ContentPanels.forEach((ContentPanel) => {
            initSwiperControl(ContentPanel);
        });
    }, 25);
});
